<template>
  <BContainer
    fluid
    class="role-dashboard security-dashboard py-3 pb-4"
  >
    <div class="d-flex justify-content-end">
      <BBreadcrumb
        v-if="$route.meta.breadcrumb"
        :items="$route.meta.breadcrumb || []"
      />
    </div>

    <AdminPageHeader
      title="Security Dashboard"
      subtitle="Track field reports, response status, and recurring incident patterns."
      icon="fas fa-shield-alt"
      gradient="green"
    />

    <BRow class="g-3 mb-4">
      <BCol
        v-for="item in metricCards"
        :key="item.label"
        cols="12"
        sm="6"
        lg="3"
      >
        <div class="kpi-card h-100">
          <div
            class="kpi-icon"
            :style="{ background: item.bg, color: item.color }"
          >
            <i :class="item.icon" />
          </div>
          <div>
            <h4 class="mb-0">
              {{ item.value }}
            </h4>
            <small>{{ item.label }}</small>
          </div>
        </div>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Resolution Rate
          </p>
          <h5 class="mb-0">
            {{ detailStats.resolutionRate }}%
          </h5>
          <small>{{ stats.resolved }} resolved of {{ stats.total }} incidents</small>
        </div>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Active Backlog
          </p>
          <h5 class="mb-0">
            {{ detailStats.openBacklog }}
          </h5>
          <small>pending incidents awaiting closure</small>
        </div>
      </BCol>
      <BCol
        cols="12"
        md="4"
      >
        <div class="detail-card h-100">
          <p class="detail-label mb-1">
            Dismissal Ratio
          </p>
          <h5 class="mb-0">
            {{ detailStats.dismissalRate }}%
          </h5>
          <small>{{ stats.dismissed }} dismissed incidents</small>
        </div>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        lg="4"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <h6 class="fw-bold mb-3">
              Incident Status
            </h6>
            <apexchart
              type="donut"
              height="280"
              :options="statusChartOptions"
              :series="statusChartSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>
      <BCol
        cols="12"
        lg="8"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Report Trend
              </h6>
              <small class="text-muted">Last 6 months</small>
            </div>
            <apexchart
              type="area"
              height="280"
              :options="trendChartOptions"
              :series="trendChartSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <BRow class="g-3 mb-4">
      <BCol
        cols="12"
        lg="5"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <h6 class="fw-bold mb-3">
              Frequent Violation Types
            </h6>
            <apexchart
              type="bar"
              height="260"
              :options="typeChartOptions"
              :series="typeChartSeries"
            />
          </BCardBody>
        </BCard>
      </BCol>
      <BCol
        cols="12"
        lg="7"
      >
        <BCard class="dash-card h-100">
          <BCardBody>
            <div class="d-flex align-items-center justify-content-between mb-3">
              <h6 class="fw-bold mb-0">
                Recent Incidents
              </h6>
              <RouterLink
                :to="{ name: 'security.violations' }"
                class="small fw-bold"
              >
                Open list
              </RouterLink>
            </div>
            <div
              v-if="recentCases.length"
              class="d-flex flex-column gap-2"
            >
              <div
                v-for="violation in recentCases"
                :key="violation.id"
                class="list-row"
              >
                <div>
                  <p class="mb-0 fw-bold small">
                    {{ violationTypeLabel(violation) }}
                  </p>
                  <small class="text-muted">
                    {{ dateLabel(violation) }}
                  </small>
                </div>
                <BBadge :variant="statusVariant(violation.status)">
                  {{ violation.status || 'pending' }}
                </BBadge>
              </div>
            </div>
            <div
              v-else
              class="empty-state"
            >
              No incidents reported yet.
            </div>
          </BCardBody>
        </BCard>
      </BCol>
    </BRow>

    <BCard class="dash-card">
      <BCardBody>
        <h6 class="fw-bold mb-3">
          Quick Actions
        </h6>
        <BRow class="g-2">
          <BCol
            cols="12"
            md="4"
          >
            <RouterLink
              :to="{ name: 'security.violation-report' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-file-signature me-2 text-success" /> Report New Incident
            </RouterLink>
          </BCol>
          <BCol
            cols="12"
            md="4"
          >
            <RouterLink
              :to="{ name: 'security.violations' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-list-alt me-2 text-warning" /> View All Reports
            </RouterLink>
          </BCol>
          <BCol
            cols="12"
            md="4"
          >
            <RouterLink
              :to="{ name: 'shared.account.settings' }"
              class="quick-link btn btn-light w-100 text-start"
            >
              <i class="fas fa-user-cog me-2 text-primary" /> Account Settings
            </RouterLink>
          </BCol>
        </BRow>
      </BCardBody>
    </BCard>
  </BContainer>
</template>

<script>
import { SSharedViolation } from '@/services/shared'
import AdminPageHeader from '@/components/core/AdminPageHeader'

const STATUS_KEYS = ['pending', 'resolved', 'dismissed']

export default {
  name: 'SecurityDashboard',

  components: {
    AdminPageHeader
  },

  middleware: ['auth', 'security-personel'],

  metaInfo: () => ({ title: 'Security Dashboard' }),

  data () {
    return {
      stats: {
        pending: 0,
        resolved: 0,
        dismissed: 0,
        total: 0
      },
      recentCases: [],
      detailStats: {
        resolutionRate: 0,
        openBacklog: 0,
        dismissalRate: 0
      },
      isDarkMode: false,
      themeObserver: null,
      statusChartSeries: [0, 0, 0],
      trendChartSeries: [{
        name: 'Incidents',
        data: [0, 0, 0, 0, 0, 0]
      }],
      typeChartSeries: [{
        name: 'Reports',
        data: [0]
      }],
      typeChartLabels: ['No Data']
    }
  },

  computed: {
    metricCards () {
      return [
        { label: 'Pending', value: this.stats.pending, icon: 'fas fa-hourglass-half', color: '#b45309', bg: 'rgba(245, 158, 11, 0.16)' },
        { label: 'Resolved', value: this.stats.resolved, icon: 'fas fa-check-circle', color: '#0f766e', bg: 'rgba(20, 184, 166, 0.16)' },
        { label: 'Dismissed', value: this.stats.dismissed, icon: 'fas fa-times-circle', color: '#be123c', bg: 'rgba(244, 63, 94, 0.16)' },
        { label: 'Total Incidents', value: this.stats.total, icon: 'fas fa-shield-alt', color: '#1d4ed8', bg: 'rgba(59, 130, 246, 0.16)' }
      ]
    },

    statusChartOptions () {
      return {
        labels: ['Pending', 'Resolved', 'Dismissed'],
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        legend: { position: 'bottom' },
        colors: ['#f9cfa7', '#b8e3c2', '#f7b4bf'],
        plotOptions: {
          pie: {
            donut: {
              size: '62%'
            }
          }
        },
        dataLabels: {
          formatter: value => `${Math.round(value)}%`
        }
      }
    },

    trendChartOptions () {
      return {
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        chart: { toolbar: { show: false }, zoom: { enabled: false } },
        colors: ['#b7c8f8'],
        stroke: { curve: 'smooth', width: 3 },
        dataLabels: { enabled: false },
        fill: {
          type: 'gradient',
          gradient: {
            shadeIntensity: 0.25,
            opacityFrom: 0.45,
            opacityTo: 0.05,
            stops: [0, 90, 100]
          }
        },
        xaxis: {
          categories: this.lastMonthLabels(6),
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        yaxis: {
          min: 0,
          forceNiceScale: true,
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        grid: { borderColor: this.isDarkMode ? '#334155' : '#dbeafe' },
        tooltip: { theme: this.isDarkMode ? 'dark' : 'light' }
      }
    },

    typeChartOptions () {
      return {
        theme: {
          mode: this.isDarkMode ? 'dark' : 'light'
        },
        chart: { toolbar: { show: false } },
        plotOptions: {
          bar: {
            horizontal: true,
            borderRadius: 6
          }
        },
        colors: ['#c7c4fa'],
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.typeChartLabels,
          labels: {
            style: {
              colors: this.isDarkMode ? '#cbd5e1' : '#64748b'
            }
          }
        },
        grid: { borderColor: this.isDarkMode ? '#334155' : '#dbeafe' },
        tooltip: { theme: this.isDarkMode ? 'dark' : 'light' }
      }
    }
  },

  mounted () {
    this.syncColorMode()
    this.setupColorModeObserver()
    this.fetchViolationStats()
  },

  beforeDestroy () {
    if (this.themeObserver) {
      this.themeObserver.disconnect()
      this.themeObserver = null
    }
  },

  methods: {
    async fetchViolationStats () {
      try {
        const { data } = await SSharedViolation.myViolations({ page: 1, per_page: 250 })
        const rows = data?.data || data?.items || []

        this.stats.pending = rows.filter(item => item.status === 'pending').length
        this.stats.resolved = rows.filter(item => item.status === 'resolved').length
        this.stats.dismissed = rows.filter(item => item.status === 'dismissed').length
        this.stats.total = rows.length

        const denominator = this.stats.total || 1
        this.detailStats = {
          resolutionRate: Math.round((this.stats.resolved / denominator) * 100),
          openBacklog: this.stats.pending,
          dismissalRate: Math.round((this.stats.dismissed / denominator) * 100)
        }

        this.statusChartSeries = STATUS_KEYS.map(key => this.stats[key])
        this.recentCases = rows.slice(0, 5)

        this.buildTrendSeries(rows)
        this.buildViolationTypeSeries(rows)
      } catch {
        this.stats.pending = 0
        this.stats.resolved = 0
        this.stats.dismissed = 0
        this.stats.total = 0
        this.detailStats = {
          resolutionRate: 0,
          openBacklog: 0,
          dismissalRate: 0
        }
      }
    },

    buildTrendSeries (rows) {
      const keys = []
      const now = new Date()

      for (let index = 5; index >= 0; index--) {
        const cursor = new Date(now.getFullYear(), now.getMonth() - index, 1)
        const month = `${cursor.getMonth() + 1}`.padStart(2, '0')
        keys.push(`${cursor.getFullYear()}-${month}`)
      }

      const buckets = keys.reduce((acc, key) => {
        acc[key] = 0
        return acc
      }, {})

      rows.forEach(row => {
        const dateValue = row?.created_at || row?.violation_date
        const date = new Date(dateValue)

        if (Number.isNaN(date.getTime())) {
          return
        }

        const month = `${date.getMonth() + 1}`.padStart(2, '0')
        const key = `${date.getFullYear()}-${month}`

        if (buckets[key] !== undefined) {
          buckets[key] += 1
        }
      })

      this.trendChartSeries = [{
        name: 'Incidents',
        data: keys.map(key => buckets[key])
      }]
    },

    buildViolationTypeSeries (rows) {
      const typeCounter = {}

      rows.forEach(row => {
        const key = this.violationTypeLabel(row)
        typeCounter[key] = (typeCounter[key] || 0) + 1
      })

      const sortedEntries = Object.entries(typeCounter)
        .sort((left, right) => right[1] - left[1])
        .slice(0, 5)

      if (!sortedEntries.length) {
        this.typeChartLabels = ['No Data']
        this.typeChartSeries = [{ name: 'Reports', data: [0] }]
        return
      }

      this.typeChartLabels = sortedEntries.map(([label]) => label)
      this.typeChartSeries = [{
        name: 'Reports',
        data: sortedEntries.map(([, value]) => value)
      }]
    },

    lastMonthLabels (count) {
      const labels = []
      const now = new Date()

      for (let index = count - 1; index >= 0; index--) {
        const cursor = new Date(now.getFullYear(), now.getMonth() - index, 1)
        labels.push(cursor.toLocaleString('default', { month: 'short' }))
      }

      return labels
    },

    statusVariant (status) {
      switch (status) {
        case 'resolved': return 'success'
        case 'dismissed': return 'danger'
        case 'pending': return 'warning'
        default: return 'secondary'
      }
    },

    violationTypeLabel (violation) {
      return violation?.violation_type?.name || violation?.violationType?.name || 'Uncategorized'
    },

    dateLabel (violation) {
      const value = violation?.created_at || violation?.violation_date
      if (!value) {
        return 'No timestamp'
      }

      const date = new Date(value)
      return Number.isNaN(date.getTime()) ? value : date.toLocaleString()
    },

    syncColorMode () {
      this.isDarkMode = document.body.classList.contains('dark')
    },

    setupColorModeObserver () {
      this.themeObserver = new MutationObserver(() => {
        this.syncColorMode()
      })

      this.themeObserver.observe(document.body, {
        attributes: true,
        attributeFilter: ['class']
      })
    }
  }
}
</script>

<style scoped>
.role-dashboard {
  --panel-border: #e2e8f0;
  background: radial-gradient(circle at 0% 0%, #eff6ff, #f8fafc 38%, #eef2ff 100%);
}

:global(body.dark) .security-dashboard {
  --panel-border: #334155;
  background: radial-gradient(circle at 0% 0%, #1f2937 0%, #111827 45%, #0f172a 100%);
}

.hero-panel {
  display: flex;
  align-items: center;
  gap: 0.85rem;
  color: #fff;
  border-radius: 1rem;
  padding: 1rem 1.1rem;
  background: linear-gradient(120deg, #0f172a, #1d4ed8 58%, #0284c7);
  box-shadow: 0 16px 28px rgba(30, 58, 138, 0.28);
}

.hero-mark {
  width: 44px;
  height: 44px;
  min-width: 44px;
  border-radius: 0.7rem;
  background: rgba(255, 255, 255, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
}

.hero-panel h2 {
  margin: 0;
  font-size: 1.15rem;
  font-weight: 700;
}

.hero-panel p {
  margin: 0;
  font-size: 0.82rem;
  opacity: 0.92;
}

.kpi-card {
  border: 1px solid var(--panel-border);
  border-radius: 0.9rem;
  background: #fff;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.8rem;
}

:global(body.dark) .security-dashboard .kpi-card,
:global(body.dark) .security-dashboard .dash-card,
:global(body.dark) .security-dashboard .detail-card {
  background: #1e293b;
}

.kpi-icon {
  width: 42px;
  height: 42px;
  min-width: 42px;
  border-radius: 0.7rem;
  display: flex;
  align-items: center;
  justify-content: center;
}

.kpi-card h4 {
  margin: 0;
  color: #0f172a;
  font-size: 1.2rem;
  font-weight: 700;
}

.kpi-card small {
  color: #94a3b8;
}

.dash-card {
  border: 1px solid var(--panel-border);
  border-radius: 1rem;
  box-shadow: 0 12px 20px rgba(15, 23, 42, 0.04);
}

:global(body.dark) .security-dashboard .dash-card {
  box-shadow: 0 14px 24px rgba(2, 6, 23, 0.35);
}

.list-row {
  border: 1px solid #e2e8f0;
  border-radius: 0.75rem;
  padding: 0.65rem 0.75rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75rem;
}

:global(body.dark) .security-dashboard .list-row {
  border-color: #475569;
  background: rgba(148, 163, 184, 0.08);
}

.empty-state {
  border: 1px dashed #cbd5e1;
  border-radius: 0.75rem;
  color: #94a3b8;
  text-align: center;
  padding: 1rem;
}

:global(body.dark) .security-dashboard .empty-state {
  border-color: #64748b;
  color: #cbd5e1;
}

.quick-link {
  border: 1px solid #e2e8f0;
  border-radius: 0.72rem;
}

:global(body.dark) .security-dashboard .quick-link {
  background: #223149;
  border-color: #475569;
  color: #e2e8f0;
}

.detail-card {
  border: 1px solid var(--panel-border);
  border-radius: 0.9rem;
  background: rgba(255, 255, 255, 0.72);
  backdrop-filter: blur(2px);
  padding: 0.85rem 0.95rem;
}

.detail-card h5 {
  color: #0f172a;
  font-weight: 700;
}

:global(body.dark) .security-dashboard .detail-card h5 {
  color: #e2e8f0;
}

.detail-card small {
  color: #64748b;
}

:global(body.dark) .security-dashboard .detail-card small {
  color: #bfd4f6;
}

.detail-label {
  color: #334155;
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

:global(body.dark) .security-dashboard .detail-label {
  color: #dbeafe;
}

@media (min-width: 992px) {
  .hero-panel h2 {
    font-size: 1.4rem;
  }

  .hero-panel p {
    font-size: 0.9rem;
  }
}
</style>
